import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Transition } from 'react-transition-group';
import { tabbable } from 'tabbable';
import BodyScroll from '@telia-front/body-scroll';
import CloseButton from '@telia-front/close-button';
import Heading from '@telia-front/heading';
import { useIsMounted } from '@telia-front/helper';
import Image from '@telia-front/image';
import { useLabels } from '@telia-front/label-provider';
import PageAccess from '@telia-front/page-access';
import Spinner from '@telia-front/spinner';
import { useTheme } from '@telia-front/theme-provider';
import Video from '@telia-front/video';
import { LightboxContext } from '../lightbox-provider/lightbox-provider';
const Lightbox = (props) => {
    const labels = useLabels();
    const { close, id, isOpen } = React.useContext(LightboxContext);
    const isMounted = useIsMounted();
    const { portalRoot } = useTheme();
    const inner = React.createElement(LightboxInner, Object.assign({}, props, { id: id, isOpen: isOpen, onClose: close, isMounted: isMounted, labels: labels }));
    return isMounted ? ReactDOM.createPortal(inner, portalRoot || document.body) : inner;
};
export default Lightbox;
const LightboxInner = (props) => {
    const { className, id, isMounted, isOpen, onClose, title } = props;
    const element = React.useRef(null);
    const [isScrollDisabled, setIsScrollDisabled] = React.useState(false);
    const BEM = (state) => {
        const classArray = ['lightbox', `is-${state}`];
        if (className) {
            classArray.push(className);
        }
        return classArray.join(' ');
    };
    const close = React.useCallback((event) => {
        event.preventDefault();
        onClose(event);
    }, [onClose]);
    const handleBackdropClick = React.useCallback((event) => {
        if (event.target === event.currentTarget) {
            onClose(event);
        }
    }, [onClose]);
    const handleMediaLoad = React.useCallback(() => {
        // wait for content render to finish by pushing the focus logic to end of queue
        window.setTimeout(() => {
            if (isScrollDisabled) {
                /* istanbul ignore else */
                if (element.current) {
                    const firstTabbableElement = tabbable(element.current)[0];
                    /* istanbul ignore else */
                    if (firstTabbableElement) {
                        firstTabbableElement.focus();
                        return;
                    }
                    element.current.focus();
                }
            }
        });
    }, [isScrollDisabled]);
    const handleKeyUp = React.useCallback((event) => {
        switch (event.key) {
            case 'Esc':
            case 'Escape': {
                onClose(event);
                break;
            }
            case 'Tab':
                /* istanbul ignore else */
                if (element.current) {
                    const tabbableElements = tabbable(element.current);
                    /* istanbul ignore else */
                    if (tabbableElements.length) {
                        const firstTabbableElement = tabbableElements[0];
                        const lastTabbableElement = tabbableElements[tabbableElements.length - 1];
                        if (event.shiftKey) {
                            /* istanbul ignore else */
                            if (document.activeElement === firstTabbableElement) {
                                event.preventDefault();
                                lastTabbableElement.focus();
                            }
                        }
                        else {
                            /* istanbul ignore else */
                            if (document.activeElement === lastTabbableElement) {
                                event.preventDefault();
                                firstTabbableElement.focus();
                            }
                        }
                    }
                    else {
                        event.preventDefault();
                    }
                }
        }
    }, [onClose]);
    const handleExited = React.useCallback(() => {
        setIsScrollDisabled(false);
    }, []);
    React.useEffect(() => {
        if (isMounted && isOpen) {
            setIsScrollDisabled(true);
        }
        return () => {
            if (isOpen && !isMounted) {
                setIsScrollDisabled(false);
            }
        };
    }, [isMounted, isOpen]);
    return (React.createElement(PageAccess, { isInert: isScrollDisabled },
        React.createElement(BodyScroll, { isDisabled: isScrollDisabled },
            React.createElement(Transition, { in: isOpen, timeout: { enter: 10, exit: 100 }, mountOnEnter: true, unmountOnExit: true, onExited: handleExited }, (state) => {
                return (React.createElement("div", { className: BEM(state), id: id, onKeyUp: handleKeyUp, tabIndex: -1, ref: element, role: "dialog", "aria-modal": true, "aria-labelledby": title ? `${id}-title` : undefined },
                    React.createElement("div", { className: "lightbox__inner", onClick: handleBackdropClick },
                        React.createElement(LightboxContent, Object.assign({}, props, { onCloseClick: close, onMediaLoad: handleMediaLoad })))));
            }))));
};
const LightboxContent = (props) => {
    const { closeButtonLabel, id, image, labels, title, video, onCloseClick, onMediaLoad } = props;
    const innerElement = React.useRef(null);
    const [hasMediaLoaded, setHasMediaLoaded] = React.useState(false);
    const [style, setStyle] = React.useState({
        maxWidth: '100%',
        width: '100%',
    });
    const BEM = () => {
        const classArray = ['lightbox__box'];
        if (hasMediaLoaded) {
            classArray.push('has-media-loaded');
        }
        return classArray.join(' ');
    };
    const setContentStyle = React.useCallback(() => {
        if (image) {
            /* istanbul ignore else */
            if (innerElement.current) {
                const nextWidth = (innerElement.current.clientWidth / innerElement.current.clientHeight) * window.innerHeight - 80;
                if (image.width) {
                    setStyle({
                        maxWidth: image.width <= 1230 ? image.width : '100%',
                        width: nextWidth,
                    });
                }
                else {
                    setStyle({
                        width: nextWidth,
                    });
                }
            }
        }
        else {
            setStyle({
                width: 1.77 * window.innerHeight - (title ? 170 : 80),
            });
        }
    }, [image, title]);
    const handleMediaLoad = React.useCallback((event) => {
        if (video || event.currentTarget.hasAttribute('srcset')) {
            setHasMediaLoaded(true);
            onMediaLoad();
        }
        setContentStyle();
    }, [setContentStyle, video, onMediaLoad]);
    React.useEffect(() => {
        const onResize = () => {
            setContentStyle();
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [setContentStyle]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: BEM(), style: style, "data-theme": "dark" },
            React.createElement("div", { className: "lightbox__box-inner", ref: innerElement },
                React.createElement(CloseButton, { className: "lightbox__close", onClick: onCloseClick, label: closeButtonLabel || labels.get('close'), theme: "dark", type: "modal" }),
                title && (React.createElement("div", { className: "lightbox__header" },
                    React.createElement(Heading, { level: 1, className: "lightbox__title h4", id: `${id}-title` }, title))),
                video && React.createElement(Video, Object.assign({}, video, { defaultPlaying: true, className: "lightbox__video", onLoad: handleMediaLoad })),
                image && React.createElement(Image, Object.assign({}, image, { className: "lightbox__image", onLoad: handleMediaLoad })))),
        !hasMediaLoaded && React.createElement(Spinner, { className: "lightbox__spinner" })));
};
